/**
 * LanguageSwitcher Component
 * 
 * Compact, professional language selector for enterprise portals.
 * Integrates with Zustand store and i18next.
 */

import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown } from 'lucide-react';

import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/shared/ui/dropdown-menu';
import { Button } from '@/shared/ui/button';
import { cn } from '@/shared/utils/commonUtils';
import { useBoundStore } from '@/stores';
import type { LanguageInfo, SupportedLanguage } from '@/stores/slices/languageSlice';
import { SuccessTickIcon } from '@/shared/ui/icons';
import { authService } from '@/features/auth/api/auth.service';

interface LanguageSwitcherProps {
    showLabel?: boolean;
    /** When false, only flag is shown (e.g. for compact header); no code, no chevron */
    showChevron?: boolean;
    className?: string;
    /** Optional class for the dropdown content (e.g. z-[80] when inside a sheet/modal) */
    dropdownContentClassName?: string;
}

/** List from store (backend is source of truth). Empty until API returns. */
function useResolvedSupportedLanguages(): LanguageInfo[] {
    const supportedLanguagesList = useBoundStore.use.supportedLanguagesList();
    return supportedLanguagesList ?? [];
}

export const LanguageSwitcher = ({
    showLabel = true,
    showChevron = true,
    className,
    dropdownContentClassName,
}: LanguageSwitcherProps) => {
    const { i18n } = useTranslation();
    const currentLanguage = useBoundStore.use.currentLanguage();
    const setLanguage = useBoundStore.use.setLanguage();
    const isAuthenticated = useBoundStore.use.isAuthenticated();
    const supportedLanguages = useResolvedSupportedLanguages();

    const handleLanguageChange = useCallback(
        (languageCode: SupportedLanguage) => {
            setLanguage(languageCode);
            i18n.changeLanguage(languageCode);
            if (isAuthenticated) {
                authService.updateLanguagePreference(languageCode).catch(() => {});
            }
        },
        [i18n, setLanguage, isAuthenticated]
    );

    const currentLangInfo = supportedLanguages.find((l) => l.code === currentLanguage);

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <Button
                    variant="ghost"
                    className={cn(
                        "flex items-center gap-2 h-9 px-3",
                        "text-gray-600 hover:text-gray-900",
                        "bg-white hover:bg-gray-50",
                        "border border-gray-200 hover:border-gray-300",
                        "rounded-lg shadow-sm",
                        "transition-all duration-150",
                        "font-medium text-sm",
                        className
                    )}
                    aria-label="Select language"
                >
                    {/* Flag and code grouped so they always stay together */}
                    <span className="flex items-center gap-1.5 shrink-0">
                        <span className="text-lg leading-none">{currentLangInfo?.flag ?? ''}</span>
                        {showLabel && (
                            <span className="text-gray-700 font-medium">{(currentLangInfo?.code ?? currentLanguage).toUpperCase()}</span>
                        )}
                    </span>
                    {showChevron && <ChevronDown className="h-3.5 w-3.5 text-gray-400 shrink-0 ms-auto" />}
                </Button>
            </DropdownMenuTrigger>

            <DropdownMenuContent
                align="end"
                sideOffset={4}
                className={cn(
                    "w-44 p-1 bg-white border border-gray-200 rounded-lg shadow-lg !outline-none !ring-0 focus:!ring-0 focus-visible:!ring-0 focus-visible:!outline-none",
                    dropdownContentClassName
                )}
            >
                {supportedLanguages.map((lang) => {
                    const isSelected = lang.code === currentLanguage;

                    return (
                        <DropdownMenuItem
                            key={lang.code}
                            onClick={() => handleLanguageChange(lang.code)}
                            className={cn(
                                "flex items-center justify-between gap-2 px-2.5 py-2 cursor-pointer rounded-none",
                                "!outline-none !ring-0 focus:!ring-0 focus-visible:!ring-0 focus-visible:!outline-none",
                                "transition-colors duration-100",
                                "!text-gray-600 hover:!bg-[#0515240f] hover:!text-gray-800 hover:shadow-[inset_2px_0_0_var(--primary)]",
                                isSelected && "!text-gray-900"
                            )}
                        >
                            <div className="flex items-center gap-2.5">
                                <span className="text-lg leading-none">{lang.flag}</span>
                                <span className="text-sm font-medium">{lang.name}</span>
                            </div>

                            {isSelected && (
                                <SuccessTickIcon className="h-4 w-4 text-green-500" />
                            )}
                        </DropdownMenuItem>
                    );
                })}
            </DropdownMenuContent>
        </DropdownMenu>
    );
};

export default LanguageSwitcher;

